import React, { useEffect, useState, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { ArrowDownToLine, ArrowUpFromLine, TrendingUp, TrendingDown, RefreshCw, WifiOff } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/Common";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const useL = () => { const { lang } = useI18n(); return (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? en ?? it) : it); };
const fmtMoney = (v, c = "EUR") => v == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: c }).format(v);
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—";

export function BalanceCard() {
  const L = useL();
  const [bal, setBal] = useState(null);
  const [btc, setBtc] = useState(null); // { price, updated_at, estimate }
  useEffect(() => { api.get("/portal/balance").then(({ data }) => setBal(data)).catch(() => setBal(false)); }, []);
  const loadPrice = useCallback(() => {
    api.get("/market/prices").then(({ data }) => {
      const p = data?.data?.BTC?.eur;
      if (p) setBtc({ price: p, updated_at: data.updated_at, estimate: data.status !== "ok" });
    }).catch(() => {});
  }, []);
  useEffect(() => {
    loadPrice();
    const id = setInterval(loadPrice, 60000);
    return () => clearInterval(id);
  }, [loadPrice]);
  const cur = bal?.currency || "EUR";
  const available = bal && bal !== false ? bal.available : null;
  const btcValue = (available != null && btc?.price) ? available / btc.price : null;
  return (
    <Card className="relative overflow-hidden p-6 lg:p-8 bg-gradient-to-br from-navy-elevated to-navy-paper border-gold/20" data-testid="balance-card">
      <div className="absolute inset-0 rc-grid-lines opacity-30" />
      <div className="relative">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{L("Saldo verificato disponibile", "Available verified balance", "Verfügbares verifiziertes Guthaben")}</p>
            <p className="mt-2 font-heading text-4xl lg:text-5xl font-light text-white" data-testid="available-balance">
              {bal === null ? "…" : bal === false ? "—" : fmtMoney(bal.available, cur)}
            </p>
            {btcValue != null && (
              <div className="mt-1.5" data-testid="balance-btc">
                <p className="text-lg text-gold font-mono">≈ {btcValue.toFixed(8)} BTC</p>
                <p className="text-[11px] text-slate-500">
                  {L("Valore BTC indicativo", "Indicative BTC value", "BTC-Wert nur als Richtwert")}
                  {btc?.estimate ? ` · ${L("ultimo prezzo disponibile", "last available price", "letzter verfügbarer Preis")}` : ""}
                  {" · "}{L("Prezzo aggiornato alle", "Price updated at", "Preis aktualisiert um")} {fmtTime(btc?.updated_at)}
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button asChild className="bg-gold text-navy hover:bg-gold-hover" data-testid="deposit-btn">
              <Link to="/portal/deposit"><ArrowDownToLine className="h-4 w-4 mr-1.5" />{L("Deposito", "Deposit", "Einzahlung")}</Link>
            </Button>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="withdraw-btn">
              <Link to="/portal/withdraw"><ArrowUpFromLine className="h-4 w-4 mr-1.5" />{L("Prelievo", "Withdraw", "Auszahlung")}</Link>
            </Button>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-4 max-w-lg">
          <div><p className="text-xs text-slate-500">{L("In sospeso", "Pending", "Ausstehend")}</p><p className="mt-1 text-lg text-slate-200" data-testid="pending-balance">{bal && bal !== false ? fmtMoney(bal.pending, cur) : "—"}</p></div>
          <div><p className="text-xs text-slate-500">{L("Riservato", "Reserved", "Reserviert")}</p><p className="mt-1 text-lg text-slate-200" data-testid="reserved-balance">{bal && bal !== false ? fmtMoney(bal.reserved, cur) : "—"}</p></div>
          <div><p className="text-xs text-slate-500">{L("Valuta", "Currency", "Währung")}</p><p className="mt-1 text-lg text-slate-200">{cur}</p></div>
        </div>
        <p className="mt-4 text-xs text-slate-600">{L("Ultimo aggiornamento", "Last update", "Letzte Aktualisierung")}: {bal && bal !== false ? fmtTime(bal.updated_at) : "—"} · {L("Saldo calcolato esclusivamente da voci di libro mastro verificate.", "Balance computed only from verified ledger entries.", "Guthaben ausschließlich aus verifizierten Hauptbucheinträgen berechnet.")}</p>
      </div>
    </Card>
  );
}

const SYMS = [
  { sym: "BTC", coin: "bitcoin", name: "Bitcoin" },
  { sym: "ETH", coin: "ethereum", name: "Ethereum" },
  { sym: "USDT", coin: "tether", name: "Tether" },
  { sym: "BNB", coin: "binancecoin", name: "BNB" },
  { sym: "XRP", coin: "ripple", name: "XRP" },
  { sym: "SOL", coin: "solana", name: "Solana" },
];

function Change({ v }) {
  if (v == null) return <span className="text-slate-500 text-xs">—</span>;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(v).toFixed(2)}%
    </span>
  );
}

export function MarketCards() {
  const L = useL();
  const [prices, setPrices] = useState(null);
  const [status, setStatus] = useState("ok");
  const [updated, setUpdated] = useState(null);
  const [spark, setSpark] = useState({});
  const load = useCallback(() => {
    api.get("/market/prices").then(({ data }) => { setPrices(data.data); setStatus(data.status); setUpdated(data.updated_at); }).catch(() => setStatus("unavailable"));
  }, []);
  useEffect(() => {
    load();
    ["bitcoin", "ethereum"].forEach((c) => api.get("/market/chart", { params: { coin: c, days: "1", vs: "eur" } })
      .then(({ data }) => setSpark((s) => ({ ...s, [c]: (data.prices || []).map((p) => ({ p: p.p })) }))).catch(() => {}));
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[SYMS[0], SYMS[1]].map((s) => {
        const d = prices?.[s.sym];
        const unavailable = status.startsWith("unavailable");
        return (
          <Card key={s.sym} className="p-5" data-testid={`market-card-${s.sym}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading text-white font-medium">{s.name} <span className="text-slate-500 text-sm">{s.sym}</span></p>
                <p className="mt-1 font-mono text-xl text-white">{d?.eur != null ? fmtMoney(d.eur, "EUR") : "—"}</p>
                <Change v={d?.change_24h} />
              </div>
              <div className="w-24 h-12">
                {spark[s.coin]?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={spark[s.coin]}><Line type="monotone" dataKey="p" stroke={d?.change_24h >= 0 ? "#34D399" : "#F87171"} strokeWidth={1.5} dot={false} /></LineChart>
                  </ResponsiveContainer>
                ) : null}
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-600 flex items-center gap-1">
              {unavailable ? <><WifiOff className="h-3 w-3" />{L("Dati di mercato temporaneamente non disponibili", "Market data temporarily unavailable", "Marktdaten vorübergehend nicht verfügbar")}</>
                : <>CoinGecko · {fmtTime(updated)}</>}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

export function MarketChart() {
  const L = useL();
  const [range, setRange] = useState("7");
  const [vs, setVs] = useState("eur");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("ok");
  const [updated, setUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/market/chart", { params: { coin: "bitcoin", days: range, vs } })
      .then(({ data }) => { setData(data.prices); setStatus(data.status); setUpdated(data.updated_at); })
      .catch(() => setStatus("unavailable")).finally(() => setLoading(false));
  }, [range, vs]);
  useEffect(() => { load(); }, [load]);

  const ranges = [["1", "24H"], ["7", "7D"], ["30", "30D"], ["365", "1Y"]];
  return (
    <Card className="p-6" data-testid="market-chart">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-heading text-lg text-white">{L("Andamento BTC", "BTC price trend", "BTC-Kursverlauf")}</h3>
          <p className="text-xs text-slate-600">{L("Informativo. Non rappresenta performance di investimento o risultati di recupero.", "Informational only. Not investment performance or recovery results.", "Nur zur Information. Keine Anlageperformance oder Wiederherstellungsergebnisse.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-white/10 overflow-hidden">
            {ranges.map(([v, lbl]) => (
              <button key={v} onClick={() => setRange(v)} data-testid={`range-${lbl}`} className={`px-2.5 py-1 text-xs ${range === v ? "bg-cyanx/20 text-cyanx" : "text-slate-400 hover:bg-white/5"}`}>{lbl}</button>
            ))}
          </div>
          <select value={vs} onChange={(e) => setVs(e.target.value)} className="h-8 rounded-md bg-navy border border-white/10 px-2 text-xs text-white" data-testid="chart-currency">
            <option value="eur">EUR</option><option value="usd">USD</option>
          </select>
          <button onClick={load} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-white/10 text-slate-400 hover:bg-white/5"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {loading ? <div className="h-64 flex items-center justify-center text-slate-500">{L("Caricamento…", "Loading…", "Wird geladen…")}</div>
        : status.startsWith("unavailable") && (!data || !data.length) ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-500 gap-2" data-testid="chart-unavailable">
            <WifiOff className="h-6 w-6" />{L("Dati di mercato temporaneamente non disponibili", "Market data temporarily unavailable", "Marktdaten vorübergehend nicht verfügbar")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F5A623" stopOpacity={0.3} /><stop offset="100%" stopColor="#F5A623" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="t" tickFormatter={(t) => new Date(t).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} stroke="#64748b" fontSize={10} minTickGap={40} />
              <YAxis stroke="#64748b" fontSize={10} domain={["auto", "auto"]} width={60} tickFormatter={(v) => v.toLocaleString()} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} labelFormatter={(t) => new Date(t).toLocaleString("it-IT")} formatter={(v) => [fmtMoney(v, vs.toUpperCase()), "BTC"]} />
              <Area type="monotone" dataKey="p" stroke="#F5A623" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      <p className="mt-3 text-[11px] text-slate-600">{status.startsWith("unavailable_cached") ? L("Dati in cache", "Cached data", "Zwischengespeicherte Daten") + " · " : "CoinGecko · "}{fmtTime(updated)}</p>
    </Card>
  );
}

export function MarketSnapshot() {
  const L = useL();
  const [prices, setPrices] = useState(null);
  const [status, setStatus] = useState("ok");
  useEffect(() => { api.get("/market/prices").then(({ data }) => { setPrices(data.data); setStatus(data.status); }).catch(() => setStatus("unavailable")); }, []);
  return (
    <Card className="p-6" data-testid="market-snapshot">
      <h3 className="font-heading text-lg text-white mb-4">{L("Panoramica di mercato", "Market snapshot", "Marktübersicht")}</h3>
      <div className="space-y-1">
        {SYMS.map((s) => {
          const d = prices?.[s.sym];
          return (
            <div key={s.sym} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
              <span className="text-slate-300 w-16">{s.sym}</span>
              <span className="font-mono text-slate-200">{d?.eur != null ? fmtMoney(d.eur, "EUR") : "—"}</span>
              <Change v={d?.change_24h} />
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-slate-600">{status.startsWith("unavailable") ? L("Non disponibile", "Unavailable", "Nicht verfügbar") : "CoinGecko"}</p>
    </Card>
  );
}
